import { lensClient } from './client'
import type {
  LensScenario, LensRun, LensCupSchema, LensCup,
  LensGoalType, LensFeatureTrait, LensFeatureRelation,
  LensTargetConfig, LensFilterCondition,
  LensDataSource, LensHop, LensAggregateConfig,
  LensApiKey, LensApiKeyCreated,
} from '../types'

export const getLensApiKeys = () => lensClient.get<LensApiKey[]>('/keys')
export const createLensApiKey = (data: { name: string; lifetimeYears: 1 | 2 | 3 }) =>
  lensClient.post<LensApiKeyCreated>('/keys', data)
export const revokeLensApiKey = (id: string) => lensClient.delete(`/keys/${id}`)

export async function getLensCups(): Promise<LensCup[]> {
  const { data } = await lensClient.get('/cups')
  return data
}

export async function getLensCupSchema(cupId: string): Promise<LensCupSchema> {
  const { data } = await lensClient.get('/scenarios/schema/cup', {
    params: { cup_id: cupId },
  })
  return data
}

export async function getScenarios(): Promise<LensScenario[]> {
  const { data } = await lensClient.get('/scenarios')
  return data
}

export async function getScenario(id: string): Promise<LensScenario> {
  const { data } = await lensClient.get(`/scenarios/${id}`)
  return data
}

export async function createScenario(payload: {
  name: string
  cup_id: string
  cup_title: string
  goal_type: LensGoalType
  feature_traits: LensFeatureTrait[]
  feature_relations: LensFeatureRelation[]
  target_config: LensTargetConfig
  filters: LensFilterCondition[]
  hyperparams: Record<string, unknown>
  datasource_id?: string | null
}): Promise<LensScenario> {
  const { data } = await lensClient.post('/scenarios', payload)
  return data
}

export async function updateScenario(id: string, payload: {
  name?: string
  feature_traits?: LensFeatureTrait[]
  feature_relations?: LensFeatureRelation[]
  target_config?: LensTargetConfig
  filters?: LensFilterCondition[]
  hyperparams?: Record<string, unknown>
}): Promise<LensScenario> {
  const { data } = await lensClient.put(`/scenarios/${id}`, payload)
  return data
}

export async function deleteScenario(id: string): Promise<void> {
  await lensClient.delete(`/scenarios/${id}`)
}

export async function startRun(scenarioId: string): Promise<LensRun> {
  const { data } = await lensClient.post(`/scenarios/${scenarioId}/runs`)
  return data
}

export async function getRun(runId: string): Promise<LensRun> {
  const { data } = await lensClient.get(`/runs/${runId}`)
  return data
}

export async function applyRun(runId: string): Promise<LensRun> {
  const { data } = await lensClient.post(`/runs/${runId}/apply`)
  return data
}

export async function deleteRun(runId: string): Promise<void> {
  await lensClient.delete(`/runs/${runId}`)
}

// ── DataSource API ────────────────────────────────────────────────────────────

export async function getDataSources(): Promise<LensDataSource[]> {
  const { data } = await lensClient.get('/datasources')
  return data
}

export async function getDataSource(id: string): Promise<LensDataSource> {
  const { data } = await lensClient.get(`/datasources/${id}`)
  return data
}

export async function createDataSource(payload: {
  name: string
  description?: string
  root_cup_id: string
  root_cup_title: string
  output_mode: 'enrich' | 'aggregate'
  hops: LensHop[]
  aggregate_config?: LensAggregateConfig | null
}): Promise<LensDataSource> {
  const { data } = await lensClient.post('/datasources', payload)
  return data
}

export async function updateDataSource(id: string, payload: {
  name?: string
  description?: string
  output_mode?: 'enrich' | 'aggregate'
  hops?: LensHop[]
  aggregate_config?: LensAggregateConfig | null
}): Promise<LensDataSource> {
  const { data } = await lensClient.put(`/datasources/${id}`, payload)
  return data
}

export async function deleteDataSource(id: string): Promise<void> {
  await lensClient.delete(`/datasources/${id}`)
}

export async function refreshDataSource(id: string): Promise<{ cached_at: string; n_rows: number; columns: { name: string; type: string }[] }> {
  const { data } = await lensClient.post(`/datasources/${id}/refresh`)
  return data
}

export async function previewDataSource(id: string): Promise<{ columns: string[]; rows: unknown[][] }> {
  const { data } = await lensClient.get(`/datasources/${id}/preview`)
  return data
}
